import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { debounce, isString, unset } from 'lodash'
import { isMoment } from 'moment'
import { useSelector } from 'react-redux'

import { getAllInternships } from '../../../../utils/api/internship'
import { DATE_WITHOUT_TIME } from '../../../../utils/constants'
import { TableParameters } from '../../../../utils/entities/tableParameters'
import InternshipsTable from './InternshipsTable'
import InternshipProvider from '../../../../Context/InternshipsManager/InternshipProvider'
import InternshipsManagerProvider from '../../../../Context/InternshipsManager/InternshipsManagerProvider'
import { DEFAULT_INTERNSHIP_FILTERS, GlobalFiltersContext } from '../../../../Providers/GlobalFiltersProvider'
import { getStudentFreeFields } from '../../../../utils/api/studentFreeField'
import { selectUser } from '../../../../reducers/UserReducer'
import { retrieveMetadata } from '../../../../utils/http'
import { API_DATE, getTimezoneLessMoment } from '../../../../utils/momentjs'
import { isNonEmptyArray } from '../../../../utils'
import { INTERNSHIPS_MANAGER } from '../../../../reducers/InternshipsManagerReducer/actionsType'

const InternshipsListView = () => {
  const { internshipDate, internshipFilters, internshipSearch, setInternshipDate, setInternshipFilters, setInternshipSearch } = useContext(GlobalFiltersContext)
  const { areStartedExcluded, institutions, schools, sections, states: status, weeksShownCount, ownership } = internshipFilters
  const user = useSelector(selectUser)

  const [internships, setInternships] = useState([])
  const [loading, setLoading] = useState(false)
  const [tableParameters, setTableParameters] = useState(new TableParameters({ order: { by: 'startDate', direction: 'desc', pageSize: 10 } }))

  const filters = useMemo(() => (
    { areStartedExcluded, institutions, schools, sections, status, weeksShownCount, ownership, search: internshipSearch, isDraft: false }
  ), [areStartedExcluded, institutions, schools, sections, status, weeksShownCount, ownership, internshipSearch])

  const period = useMemo(() => {
    const period = { startDate: null, endDate: null }

    if (isMoment(internshipDate)) {
      period.startDate = internshipDate
      period.endDate = internshipDate.clone().add((weeksShownCount ?? 12) - 1, 'week').endOf('week')
    }

    return period
  }, [internshipDate ? internshipDate.format(DATE_WITHOUT_TIME) : null, weeksShownCount])

  const parameters = useMemo(() => new TableParameters({
    ...tableParameters,
    search: filters.search ?? '',
    order: tableParameters.order,
    filters: filters,
    period: period
  }), [filters, period, tableParameters])

  useEffect(() => {
    const fetch = debounce(() => {
      setLoading(true)

      fetchInternships(new TableParameters({ ...tableParameters, filters, period }))
    }, 300)

    fetch()

    return () => {
      fetch.cancel()
    }
  }, [filters, period, tableParameters.page, tableParameters.pageSize, tableParameters.order?.by, tableParameters.order?.direction])

  const fetchInternships = useCallback(parameters => {
    const formattedParameters = parameters.toQueryParameters()

    formattedParameters.startDate = parameters.period.startDate
    formattedParameters.endDate = parameters.period.endDate
    formattedParameters.contexts = ['coordinator', 'school']
    formattedParameters.states = parameters.filters.status

    if (isString(parameters.filters?.search)) {
      formattedParameters.search = formattedParameters.filters.search
    }

    unset(formattedParameters, 'filters[states]')

    getAllInternships(user, formattedParameters).then(({ data, meta }) => {
      if (data) {
        const metadata = retrieveMetadata(meta)
        setInternships(data)
        setTableParameters(prev => new TableParameters({ ...prev, pageSize: metadata.pageSize, totalPages: metadata.totalPages, metadata }))
        getFreeFields(data)
      }
    }).finally(() => setLoading(false))
  }, [user])

  const getFreeFields = useCallback((internships) => {
    if (isNonEmptyArray(internships)) {
      getStudentFreeFields(user, internships.map(i => i.student?.id ?? null).filter(i => i !== null)).then(({ data }) => {
        const newData = internships.map(internship => {
          const studentId = internship.student?.id ?? -1
          const freeFields = data[studentId]
          return {
            ...internship,
            student: {
              ...internship.student,
              freeFields: freeFields || {}
            }
          }
        })
        setInternships(newData)
      })
    }
  }, [])

  const handleParametersChange = useCallback(newParameters => {
    setInternshipSearch(newParameters.filters?.search ?? '')
    setInternshipDate(newParameters.period?.startDate ?? null)

    const newStart = newParameters.period?.startDate ?? null

    if (
      newParameters.filters?.search !== parameters.filters.search ||
      (newStart && parameters.period?.startDate
        ? getTimezoneLessMoment(newStart).format(API_DATE) !== getTimezoneLessMoment(parameters.period.startDate).format(API_DATE)
        : newStart !== parameters.period?.startDate)
    ) {
      setTableParameters(prev => ({ ...prev, ...newParameters, page: 1 }))
    } else {
      setTableParameters(prev => ({ ...prev, ...newParameters }))
    }
  }, [parameters])

  const handleFilterChange = () => {
    setTableParameters({ ...tableParameters, page: 1 })
  }

  const handleResetFilters = () => {
    setTableParameters({ ...tableParameters, page: 1 })
    setInternshipSearch('')
    setInternshipFilters(DEFAULT_INTERNSHIP_FILTERS)
  }

  return (
    <InternshipsManagerProvider>
      <InternshipProvider refreshQuotas={() => fetchInternships(parameters)}>
        <InternshipsTable
          data={internships}
          loading={loading}
          parameters={parameters}
          storageKey={`${INTERNSHIPS_MANAGER.LIST_VIEW_TABLE_DISPLAY}-${user.id}`}
          onParametersChange={handleParametersChange}
          onDataChange={fetchInternships}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
        />
      </InternshipProvider>
    </InternshipsManagerProvider>

  )
}

export default InternshipsListView
